import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

interface DataTableColumn<T> {
  readonly header: string;
  readonly accessor: keyof T | ((row: T) => ReactNode);
  readonly align?: "left" | "right" | "center";
}

interface DataTableProps<T> {
  readonly columns: readonly DataTableColumn<T>[];
  readonly rows: readonly T[];
  readonly getRowKey: (row: T) => string;
}

function useZebraColors() {
  const theme = useTheme();
  const mode = theme.palette.mode;
  return {
    even:
      mode === "dark"
        ? theme.palette.surfaceContainerLow
        : theme.palette.surfaceContainerLow,
    odd:
      mode === "dark"
        ? theme.palette.surfaceContainer
        : theme.palette.background.paper,
    hover: theme.palette.surfaceContainerHigh,
    headerColor: theme.palette.onSurfaceVariant,
  };
}

export default function DataTable<T>({
  columns,
  rows,
  getRowKey,
}: DataTableProps<T>) {
  const zebra = useZebraColors();

  return (
    <TableContainer>
      <Table size="small">
        <DataTableHead columns={columns} headerColor={zebra.headerColor} />
        <TableBody>
          {rows.map((row, idx) => (
            <DataTableRow
              key={getRowKey(row)}
              row={row}
              columns={columns}
              bg={idx % 2 === 0 ? zebra.even : zebra.odd}
              hoverBg={zebra.hover}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DataTableHead<T>({
  columns,
  headerColor,
}: {
  readonly columns: readonly DataTableColumn<T>[];
  readonly headerColor: string;
}) {
  return (
    <TableHead>
      <TableRow>
        {columns.map((col) => (
          <TableCell
            key={col.header}
            align={col.align}
            sx={{
              color: headerColor,
              fontWeight: 500,
              fontSize: "0.6875rem",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              borderBottom: "none",
              pb: 1,
            }}
          >
            {col.header}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function DataTableRow<T>({
  row,
  columns,
  bg,
  hoverBg,
}: {
  readonly row: T;
  readonly columns: readonly DataTableColumn<T>[];
  readonly bg: string;
  readonly hoverBg: string;
}) {
  return (
    <TableRow sx={{ backgroundColor: bg, "&:hover": { backgroundColor: hoverBg } }}>
      {columns.map((col) => (
        <TableCell key={col.header} align={col.align} sx={{ borderBottom: "none" }}>
          {typeof col.accessor === "function"
            ? col.accessor(row)
            : (row[col.accessor] as ReactNode)}
        </TableCell>
      ))}
    </TableRow>
  );
}
